// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Modal, StatusBanner } from '@loa/ui-components';
import { bulkPublish } from '../../api/signatoryStatusApi';
import type { BulkOperationResult } from '../../types/signatoryStatus';

type BulkPublishConfirmModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  profileId: string;
  draftCount: number;
  clientId?: string;
}>;

export function BulkPublishConfirmModal({
  open,
  onClose,
  onSuccess,
  profileId,
  draftCount,
  clientId,
}: BulkPublishConfirmModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkOperationResult | null>(null);

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError(null);
      setResult(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const operationResult = await bulkPublish(profileId, {
        clientId: clientId || undefined,
      });
      setResult(operationResult);
      if (operationResult.succeeded > 0) {
        onSuccess();
      }
    } catch (err) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 400) {
        setError('No eligible signatories found to publish. Please refresh and try again.');
      } else if (axiosErr.response?.status === 404) {
        setError('Profile not found. Please refresh and try again.');
      } else {
        setError('Failed to publish signatories. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (result) {
    return (
      <Modal open={open} onClose={handleClose} size="medium">
        <Modal.Header title="Bulk Publish Result" onClose={handleClose} />
        <Modal.Content>
          {result.succeeded > 0 && (
            <StatusBanner
              variant="success"
              message={<>Successfully activated <strong>{result.succeeded}</strong> of <strong>{result.total}</strong> signatories for signing.</>}
              visible
              position="inline"
              dismissible={false}
            />
          )}
          {result.failed > 0 && (
            <StatusBanner
              variant="error"
              message={<><strong>{result.failed}</strong> signatories failed to activate.</>}
              visible
              position="inline"
              dismissible={false}
            />
          )}
          {result.skipped > 0 && (
            <StatusBanner
              variant="neutral"
              message={<><strong>{result.skipped}</strong> signatories were skipped (not in Draft status).</>}
              visible
              position="inline"
              dismissible={false}
            />
          )}
          {result.details.length > 0 && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '4px', fontSize: '0.9em' }}>
              <strong>Details:</strong>
              <ul aria-label="Bulk publish operation details" style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                {result.details.map((detail) => (
                  <li key={detail} style={{ marginBottom: '0.25rem' }}>{detail}</li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions
          primaryAction={{ label: 'Close', onClick: handleClose }}
        />
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} size="medium">
      <Modal.Header title="Confirm Bulk Publish" onClose={handleClose} />
      <Modal.Content>
        <StatusBanner
          variant="neutral"
          message={
            <>
              This will activate <strong>{draftCount}</strong> signatory record{draftCount !== 1 ? 's' : ''} from{' '}
              <strong>Draft</strong> to <strong>Ready for Signing</strong> status.
              {clientId && <> Only signatories for the selected client will be affected.</>}
            </>
          }
          visible
          position="inline"
          dismissible={false}
        />

        <p style={{ marginTop: '1rem' }}>
          Once activated, signatories will be ready for LOA signing. Are you sure you want to proceed?
        </p>

        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: submitting }}
        primaryAction={{
          label: 'Publish ' + (draftCount === 1 ? '1 Signatory' : draftCount + ' Signatories'),
          onClick: () => { void handleConfirm(); },
          disabled: submitting || draftCount === 0,
          loading: submitting,
        }}
      />
    </Modal>
  );
}
